// =====================================================================
// Faz AF (2026-05-07) — Aylık Hedef + Progress Tracker
// =====================================================================
// Owner ay başında ciro/sipariş/yeni müşteri hedefini koyar, sayfa MTD
// ilerleme + ay sonu projeksiyon ile karşılaştırma yapar.
//
// 3 panel:
//   1. Bu ay hedef + ilerleme (büyük progress bar)
//   2. Hedef düzenleme dialog (ay başında girilir)
//   3. 12 ay geçmiş trend bar chart (hedef vs gerçek)
//
// Endpoint:
//   GET  /api/admin/insights/goals
//   PUT  /api/admin/insights/goals
//   GET  /api/admin/insights/goals/history?months=12
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert, IconButton,
  Tooltip, Button, LinearProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";
const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");

// "2026-05" → "Mayıs 2026"
const monthLabel = (year, month) => {
  const months = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
  return `${months[month - 1]} ${year}`;
};

const monthShort = (year, month) => {
  const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  return `${months[month - 1]} ${String(year).slice(2)}`;
};

// Progress yüzdesine göre renk
const progressColor = (pct) => {
  if (pct == null) return "#94a3b8";
  if (pct >= 100) return "#10b981";  // hedef aşıldı 🎉
  if (pct >= 75)  return "#22c55e";  // çok iyi
  if (pct >= 50)  return "#3b82f6";  // normal
  if (pct >= 25)  return "#f59e0b";  // dikkat
  return "#ef4444";                   // kritik
};

export default function MonthlyGoals() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ revenue_target: 0, orders_target: 0, new_customers_target: 0, notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [goalR, histR] = await Promise.allSettled([
        API.get("/api/admin/insights/goals"),
        API.get("/api/admin/insights/goals/history?months=12")
      ]);
      setData(goalR.status === "fulfilled" ? goalR.value.data : null);
      setHistory(histR.status === "fulfilled" ? histR.value.data : null);
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };
  // useEffect(load, []) ❌ — load async olduğu için Promise döner, React onu cleanup
  // sanıp unmount'ta çağırır → "TypeError: c is not a function" (beyaz ekran).
  // Doğrusu: arrow ile sar, dönüş undefined olur, cleanup yok.
  useEffect(() => { load(); }, []);

  // Dialog aç → mevcut hedef bilgisini form'a yükle
  const openEdit = () => {
    setForm({
      revenue_target: Number(data?.goal?.revenue_target) || 0,
      orders_target: Number(data?.goal?.orders_target) || 0,
      new_customers_target: Number(data?.goal?.new_customers_target) || 0,
      notes: data?.goal?.notes || ""
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      await API.put("/api/admin/insights/goals", {
        year: data.year, month: data.month,
        ...form
      });
      toast.success("Hedefler güncellendi");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const goal = data.goal;
  const actuals = data.actuals;
  const progress = data.progress;
  const projection = data.projection;

  // Trend chart için max revenue (target + actual ikisinden büyük olanı)
  const trendMax = history?.history
    ? Math.max(...history.history.flatMap(h => [Number(h.revenue_target) || 0, Number(h.revenue_actual) || 0]), 1)
    : 1;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Aylık Hedefler</Typography>
          <Typography variant="body2" color="text.secondary">
            {monthLabel(data.year, data.month)} — hedef vs ilerleme
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Yenile"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<EditIcon />} onClick={openEdit}>
            Hedef Belirle
          </Button>
        </Box>
      </Box>

      {/* Hedef yoksa uyar */}
      {!goal && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Bu ay için hedef tanımlanmamış.</strong>
          {" "}<strong>"Hedef Belirle"</strong> butonuna basarak ay başı hedef koyabilirsin.
          MTD gerçekleşen değerler aşağıda görünür.
        </Alert>
      )}

      {/* Ay ilerleme barı (gün bazlı) */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Ay İlerlemesi</Typography>
          <Typography variant="caption" color="text.secondary">
            {progress.days_passed}/{progress.days_in_month} gün geçti · {progress.days_remaining} gün kaldı
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={(progress.days_passed / progress.days_in_month) * 100}
          sx={{ height: 10, borderRadius: 5, bgcolor: "#f1f5f9",
            "& .MuiLinearProgress-bar": { bgcolor: "#94a3b8", borderRadius: 5 }
          }}
        />
      </Card>

      {/* 3 KPI kart: Ciro / Sipariş / Yeni Müşteri */}
      <Grid container spacing={2} mb={3}>
        {/* Ciro hedefi */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            p: 2.5, borderRadius: 3, height: "100%",
            border: `2px solid ${progressColor(progress.revenue_pct)}`,
            background: `linear-gradient(135deg, ${progressColor(progress.revenue_pct)}11 0%, ${progressColor(progress.revenue_pct)}05 100%)`
          }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>CİRO HEDEFİ</Typography>
            {goal && Number(goal.revenue_target) > 0 ? (
              <>
                <Typography variant="h4" fontWeight={800} sx={{ color: progressColor(progress.revenue_pct), lineHeight: 1.1 }}>
                  %{progress.revenue_pct || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  <strong>{fmtMoney(actuals.revenue)}</strong> / {fmtMoney(goal.revenue_target)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progress.revenue_pct || 0, 100)}
                  sx={{ height: 8, borderRadius: 4, mt: 1, bgcolor: "#f1f5f9",
                    "& .MuiLinearProgress-bar": { bgcolor: progressColor(progress.revenue_pct), borderRadius: 4 }
                  }}
                />
                {data.isCurrentMonth && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
                    📊 Ay sonu projeksiyon: <strong>{fmtMoney(projection.revenue)}</strong>
                    {" "}({Number(goal.revenue_target) > 0
                      ? `hedefin %${Math.round((projection.revenue / Number(goal.revenue_target)) * 100)}'i`
                      : ""})
                  </Typography>
                )}
              </>
            ) : (
              <Typography color="text.disabled" mt={1}>Hedef tanımsız</Typography>
            )}
          </Card>
        </Grid>

        {/* Sipariş hedefi */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            p: 2.5, borderRadius: 3, height: "100%",
            border: `2px solid ${progressColor(progress.orders_pct)}`,
            background: `linear-gradient(135deg, ${progressColor(progress.orders_pct)}11 0%, ${progressColor(progress.orders_pct)}05 100%)`
          }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>SİPARİŞ HEDEFİ</Typography>
            {goal && goal.orders_target > 0 ? (
              <>
                <Typography variant="h4" fontWeight={800} sx={{ color: progressColor(progress.orders_pct), lineHeight: 1.1 }}>
                  %{progress.orders_pct || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  <strong>{fmtNum(actuals.orders)}</strong> / {fmtNum(goal.orders_target)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progress.orders_pct || 0, 100)}
                  sx={{ height: 8, borderRadius: 4, mt: 1, bgcolor: "#f1f5f9",
                    "& .MuiLinearProgress-bar": { bgcolor: progressColor(progress.orders_pct), borderRadius: 4 }
                  }}
                />
              </>
            ) : (
              <Typography color="text.disabled" mt={1}>Hedef tanımsız</Typography>
            )}
          </Card>
        </Grid>

        {/* Yeni müşteri hedefi */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            p: 2.5, borderRadius: 3, height: "100%",
            border: `2px solid ${progressColor(progress.customers_pct)}`,
            background: `linear-gradient(135deg, ${progressColor(progress.customers_pct)}11 0%, ${progressColor(progress.customers_pct)}05 100%)`
          }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>YENİ MÜŞTERİ HEDEFİ</Typography>
            {goal && goal.new_customers_target > 0 ? (
              <>
                <Typography variant="h4" fontWeight={800} sx={{ color: progressColor(progress.customers_pct), lineHeight: 1.1 }}>
                  %{progress.customers_pct || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  <strong>{fmtNum(actuals.new_customers)}</strong> / {fmtNum(goal.new_customers_target)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progress.customers_pct || 0, 100)}
                  sx={{ height: 8, borderRadius: 4, mt: 1, bgcolor: "#f1f5f9",
                    "& .MuiLinearProgress-bar": { bgcolor: progressColor(progress.customers_pct), borderRadius: 4 }
                  }}
                />
              </>
            ) : (
              <Typography color="text.disabled" mt={1}>Hedef tanımsız</Typography>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* 12 ay geçmiş trend chart */}
      {history?.history && history.history.length > 0 && (
        <Card sx={{ p: 2.5, borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <EmojiEventsIcon sx={{ color: "#f59e0b" }} />
            <Typography variant="h6" fontWeight={700}>12 Ay Hedef vs Gerçek</Typography>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 600, display: "flex", alignItems: "flex-end", gap: 1, height: 180 }}>
              {history.history.map((h, i) => (
                <Box key={i} sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                  {/* İki bar yan yana: target (gri) + actual (renkli) */}
                  <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.3, height: 140, width: "100%", justifyContent: "center" }}>
                    {Number(h.revenue_target) > 0 && (
                      <Tooltip title={`Hedef: ${fmtMoney(h.revenue_target)}`} placement="top">
                        <Box sx={{
                          flex: 1, maxWidth: 16,
                          height: `${(Number(h.revenue_target) / trendMax) * 100}%`,
                          bgcolor: "#cbd5e1", borderRadius: "2px 2px 0 0"
                        }} />
                      </Tooltip>
                    )}
                    <Tooltip title={`Gerçek: ${fmtMoney(h.revenue_actual)} ${h.achieved != null ? `(%${h.achieved})` : ""}`} placement="top">
                      <Box sx={{
                        flex: 1, maxWidth: 16,
                        height: `${(Number(h.revenue_actual) / trendMax) * 100}%`,
                        bgcolor: h.achieved >= 100 ? "#10b981" :
                                h.achieved >= 75 ? "#3b82f6" :
                                h.achieved >= 50 ? "#f59e0b" :
                                h.achieved != null ? "#ef4444" : "#94a3b8",
                        borderRadius: "2px 2px 0 0"
                      }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: 10, color: "text.disabled" }}>
                    {monthShort(h.year, h.month)}
                  </Typography>
                  {h.achieved != null && (
                    <Chip
                      label={`%${h.achieved}`}
                      size="small"
                      sx={{
                        height: 16, fontSize: 9, fontWeight: 700,
                        bgcolor: h.achieved >= 100 ? "#10b98122" : h.achieved >= 75 ? "#3b82f622" : "#f8fafc",
                        color: h.achieved >= 100 ? "#10b981" : h.achieved >= 75 ? "#3b82f6" : "#64748b"
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Legend */}
          <Box sx={{ display: "flex", gap: 2, mt: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: "#cbd5e1", borderRadius: 1 }} />
              <Typography variant="caption">Hedef</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: "#10b981", borderRadius: 1 }} />
              <Typography variant="caption">Gerçek (≥%100)</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: "#3b82f6", borderRadius: 1 }} />
              <Typography variant="caption">İyi (%75+)</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: "#ef4444", borderRadius: 1 }} />
              <Typography variant="caption">Zayıf (%50-)</Typography>
            </Box>
          </Box>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{monthLabel(data.year, data.month)} Hedefleri</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <TextField
                fullWidth type="number" label="Ciro Hedefi (₺)"
                value={form.revenue_target}
                onChange={e => setForm({ ...form, revenue_target: parseFloat(e.target.value) || 0 })}
                helperText="Bu ay toplam ulaşmak istediğin ciro"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="number" label="Sipariş Hedefi"
                value={form.orders_target}
                onChange={e => setForm({ ...form, orders_target: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth type="number" label="Yeni Müşteri Hedefi"
                value={form.new_customers_target}
                onChange={e => setForm({ ...form, new_customers_target: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Notlar"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Bu ay özel hedef nedeni, kampanya planı vb."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={submitEdit} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
